import { AuthenticationStore, Service, _getBaseStoreInstance } from '@roc/feature-app-core';

const url = {
  CHAT_STREAM: '/api/v1/ai/chat/stream',
  SEND_MESSAGE_FEEDBACK: '/api/v1/ai/chat/feedback/message',
  GET_MESSAGES: '/api/v1/ai/chat/messages',
  SEND_SESSION_FEEDBACK: '/api/v1/ai/chat/feedback/session',
  GET_INFORMATION_OPTIONS: '/api/v1/ai/chat/informationOptions',
};

export interface ChatRequest {
  sessionId: string;
  message: string;
  botType?: string;
  assistantMessageId: string;
  userMessageId?: string;
}

export class ChatBotService extends Service {
  authenticationStore: AuthenticationStore;

  constructor(authenticationStore: AuthenticationStore) {
    super();
    this.authenticationStore = authenticationStore;
  }


  chatStream(
    request: ChatRequest,
    onToken: (chunk: string, messageId?: string) => void,
    onEnd: () => void,
    onError: (error: Error) => void,
    handlers?: {
      onToolRequested?: (payload: any) => void;
      onToolExecuted?: (payload: any) => void;
      onMessageStart?: (payload: any) => void;
      onFinal?: (payload: any) => void;
      onDone?: (payload: any) => void;
      onComponent?: (payload: { type: string; messageId: string; data: Record<string, any> }) => void;
    }
  ): EventSource {
    const controller = new AbortController();

    this.streamWithFetch(
      url.CHAT_STREAM,
      request,
      onToken,
      onEnd,
      onError,
      controller,
      handlers
    );

    return {
      close: () => controller.abort(),
      readyState: EventSource.OPEN,
    } as EventSource;
  }

  private async streamWithFetch(
    url: string,
    request: ChatRequest,
    onToken: (chunk: string, messageId?: string) => void,
    onEnd: () => void,
    onError: (error: Error) => void,
    controller: AbortController,
    handlers?: {
      onToolRequested?: (payload: any) => void;
      onToolExecuted?: (payload: any) => void;
      onMessageStart?: (payload: any) => void;
      onFinal?: (payload: any) => void;
      onDone?: (payload: any) => void;
      onComponent?: (payload: { type: string; messageId: string; data: Record<string, any> }) => void;
    }
  ): Promise<void> {
    try {
      const baseURL = this.getHost();
      const fullURL = `${baseURL}${url}`;

      const jwtToken = this.authenticationStore
        ? await this.authenticationStore.getJwtToken(false)
        : null;

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      };

      if (jwtToken) {
        headers['Authorization'] = `Bearer ${jwtToken}`;
      }

      // Apply default botType if not provided
      const requestWithDefaults = {
        ...request,
        botType: request.botType || 'GENERAL_CHAT',
      };

      let response = await fetch(fullURL, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestWithDefaults),
        signal: controller.signal,
        credentials: 'include',
      });

      // Token may have expired — 406 occurs because Spring can't render
      // a 401 error as text/event-stream. Refresh token and retry once,
      // matching the axios interceptor behavior in service.ts.
      if (response.status === 401 || response.status === 406) {
        const refreshedToken = this.authenticationStore
          ? await this.authenticationStore.getJwtToken(true)
          : null;
        if (refreshedToken) {
          headers['Authorization'] = `Bearer ${refreshedToken}`;
          response = await fetch(fullURL, {
            method: 'POST',
            headers,
            body: JSON.stringify(requestWithDefaults),
            signal: controller.signal,
            credentials: 'include',
          });
        }
      }

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData?.error?.message) {
            errorMessage = errorData.error.message;
          } else if (errorData?.message) {
            errorMessage = errorData.message;
          }
        } catch (e) {
          errorMessage += ` - ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('Response body is null');
      }

      let buffer = '';

      let currentEvent = 'message';
      let currentDataLines: string[] = [];

      const flushEvent = () => {
        const evt = currentEvent || 'message';
        const data = currentDataLines.join('\n');

        // reset for next event
        currentEvent = 'message';
        currentDataLines = [];

        if (!data) return;

        if (evt === 'token') {
          try {
            // Parse JSON to restore escaped newlines (\n)
            const parsedData = JSON.parse(data);
            if (typeof parsedData === 'object') {
              onToken(parsedData.text, parsedData.messageId);
            } else {
              onToken(parsedData);
            }
          } catch (e) {
            // Fallback to raw data if not valid JSON
            console.warn('Token data is not valid JSON, using raw data:', e);
            onToken(data);
          }
          return;
        }

        if (evt === 'component') {
          try {
            const payload = JSON.parse(data);
            if (handlers?.onComponent) {
              handlers.onComponent(payload);
            }
          } catch (e) {
            console.error('Error parsing component payload:', e);
          }
          return;
        }

        if (evt === 'done') {
          onEnd();
          return;
        }

        // Handle message.start event - signals creation of new message bubble
        if (evt === 'message.start') {
          try {
            const payload = JSON.parse(data);
            if (handlers?.onMessageStart) {
              handlers.onMessageStart(payload);
            }
          } catch (e) {
            console.error('Error parsing message.start payload:', e);
          }
          return;
        }

        // Handle tool.requested event
        if (evt === 'tool.requested') {
          try {
            const payload = JSON.parse(data);
            if (handlers?.onToolRequested) {
              handlers.onToolRequested(payload);
            }
          } catch (e) {
            console.error('Error parsing tool.requested payload:', e);
          }
          return;
        }

        // Handle tool.executed event
        if (evt === 'tool.executed') {
          try {
            const payload = JSON.parse(data);
            if (handlers?.onToolExecuted) {
              handlers.onToolExecuted(payload);
            }
          } catch (e) {
            console.error('Error parsing tool.executed payload:', e);
          }
          return;
        }

        // tool.requested / message.final / error should NOT be appended to chat text
        // handle them via separate callbacks if you add them
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const rawLine of lines) {
          const line = rawLine.replace(/\r$/, '');

          if (line === '') {
            flushEvent();
            continue;
          }
          if (line.startsWith('event:')) {
            if (currentDataLines.length > 0) flushEvent();
            currentEvent = line.substring(6).trim();
            continue;
          }

          if (line.startsWith('data:')) {
            const dataPart = line.substring(5); // do NOT trim or strip leading spaces it can cause misformatting of the content
            if (dataPart.trim() === '[DONE]') {
              onEnd();
              return;
            }

            currentDataLines.push(dataPart);
            continue;
          }
        }
      }

      // flush anything pending at end of stream
      flushEvent();
      onEnd();
    } catch (error) {
      if (error.name === 'AbortError') {
        // Stream was cancelled
        return;
      }
      console.error('ChatBotService streamWithFetch error:', error);
      onError(error as Error);
    }
  }

  sendMessageFeedback(feedbackdata) {
    return this.post(
      url.SEND_MESSAGE_FEEDBACK,
      feedbackdata
    );
  }

  sendSessionFeedback(feedbackdata) {
    return this.post(
      url.SEND_SESSION_FEEDBACK,
      feedbackdata
    );
  }

  getMessagesHistory(sessionId: string, limit: number = 20, offset: number) {
    return this.get(url.GET_MESSAGES, { sessionId, limit, offset });
  }

  getInformationOptions() {
    return this.get(url.GET_INFORMATION_OPTIONS);
  }

}
